import React, { createContext, useContext, useEffect, useState } from 'react';
import { adminApi, extractError } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem('admin_user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Validate session on app start if token exists
    let active = true;
    async function validate() {
      if (!token) return;
      try {
        const res = await adminApi.get('/auth/me');
        if (active) {
          setUser(res.data);
          localStorage.setItem('admin_user', JSON.stringify(res.data));
        }
      } catch (e) {
        if (active) {
          setUser(null);
          setToken(null);
        }
      }
    }
    validate();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login(email, password) {
    setLoading(true);
    try {
      const res = await adminApi.post('/auth/login', { email, password });
      const { access_token, user: u } = res.data;
      localStorage.setItem('admin_token', access_token);
      localStorage.setItem('admin_user', JSON.stringify(u));
      setToken(access_token);
      setUser(u);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: extractError(e) };
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    // Fire-and-forget logout to backend; swallow any rejection so it doesn't
    // bubble as an unhandled promise rejection (which webpack-dev-server
    // surfaces as a runtime error overlay and blocks the login UI).
    adminApi.post('/auth/logout').catch(() => {});
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setUser(null);
    setToken(null);
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
